import { ProductCard } from "@govlaunch/product";
import {
  Anchor,
  Avatar,
  Box,
  Button,
  CountryFlag,
  Flex,
  Heading,
  IconButton,
  LoadingSpinner,
  PageGridContainer,
  SimpleGrid,
  TCountryCode,
  Text,
} from "@govlaunch/web";
import { NetworkStatus, useApolloClient, useMutation, useQuery } from "@apollo/client";
import intersectionWith from "lodash/fp/intersectionWith";
import isEqual from "lodash/fp/isEqual";
import omit from "lodash/fp/omit";
import uniqBy from "lodash/fp/uniqBy";
import Link from "next/link";
import { NextRouter, useRouter } from "next/router";
import queryString from "querystring";
import React, { createRef, forwardRef, SVGProps, useEffect, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useToggle } from "react-use";
import { useSelfie } from "~/components/auth/Auth";
import CollectionPageSeo from "~/components/seo/CollectionPageSeo";
import ToggleBookmarkMutation from "~/lib/mutations/ToggleBookmarkMutation";
import {
  IToggleBookmarkMutation,
  IToggleBookmarkMutationVariables,
} from "~/lib/mutations/__generated__/ToggleBookmarkMutation.generated";
import getCityType from "~/lib/utils/getCityType";
import { useIntlLocale } from "~/lib/withIntl";
import {
  IBookmarkType,
  ICollectionItemType,
  ICollectionPackaging,
  ICollectionSponsoring,
  ITag,
  ITagType,
} from "~/types/types";
import MultipleFilterSelect from "~/src/shared/components/MultipleFilterSelect";
import useCountryStateFilters from "~/src/shared/hooks/useCountryStateFilters";
import filterInUnionByTypenames from "~/src/utils/filterInUnionByTypenames";
import { appendNextLinkHrefParams } from "~/src/utils/nextLinkHref";
import stringOrFirst from "~/src/utils/stringOrFirst";
import useSignInModal from "~/src/components/auth/useSignInModal";
import ProjectCards from "~/src/components/project/listing/ProjectCards";
import hidePodcastTag from "~/src/components/story/pages/hidePodcastTag";
import StoryCardWithPlaceholder from "~/src/components/story/StoryCardWithPlaceholder";
import TranslationButton from "~/src/components/translation/TranslationButton";
import CollectionGroupCard from "~/src/components/collection/CollectionGroupCard";
import CollectionTabLink from "~/src/components/collection/CollectionTabLink";
import getInitialTabRedirect from "~/src/components/collection/getInitialTabRedirect";
import CollectionBySlugQuery from "~/src/components/collection/graphql/CollectionBySlugQuery";
import SelfieCollectionsQuery from "~/src/components/collection/graphql/SelfieCollectionsQuery";
import TrackLastVisitOnCollectionsMutation from "~/src/components/collection/graphql/TrackLastVisitOnCollectionsMutation";
import {
  ICollectionBySlugQuery,
  ICollectionBySlugQueryVariables,
} from "~/src/components/collection/graphql/__generated__/CollectionBySlugQuery.generated";
import {
  ITrackLastVisitOnCollectionsMutation,
  ITrackLastVisitOnCollectionsMutationVariables,
} from "~/src/components/collection/graphql/__generated__/TrackLastVisitOnCollectionsMutation.generated";
import {
  DiscussionsIcon,
  GovernmentsIcon,
  ProductsIcon,
  ProjectsIcon,
  StoriesIcon,
} from "~/src/components/collection/icons";
import {
  NoDiscussions,
  NoGovernments,
  NoProducts,
  NoProjects,
  NoStories,
} from "~/src/components/collection/placeholders";

export default function CollectionBySlugPage() {
  const router = useRouter();
  const apolloClient = useApolloClient();
  const tab = stringOrFirst(router.query.tab);
  const tags = getTagsFromRouter(router);
  const countries = getCountriesFromRouter(router);
  const initialRedirected = useRef(false);
  const selfie = useSelfie();
  const signInModal = useSignInModal();
  const intl = useIntl();
  const { locale } = useIntlLocale();

  const { data, fetchMore, loading, networkStatus } = useQuery<ICollectionBySlugQuery, ICollectionBySlugQueryVariables>(
    CollectionBySlugQuery,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        cursor: null,
        countries,
        slug: stringOrFirst(router.query.collectionSlug),
        filterByItemType: getFilterFromTab(tab),
        tags,
      },
    },
  );

  useEffect(() => {
    if (data && data.collectionBySlug && data.collectionBySlug._id) {
      if (selfie) {
        apolloClient.mutate<ITrackLastVisitOnCollectionsMutation, ITrackLastVisitOnCollectionsMutationVariables>({
          mutation: TrackLastVisitOnCollectionsMutation,
          variables: {
            collectionIds: [data.collectionBySlug._id],
          },
        });
      }

      const redirect = getInitialTabRedirect(data.collectionBySlug.contentCounters);

      if (redirect && !initialRedirected.current && !tab) {
        router.push("/collections/[collectionSlug]/[[...tab]]", `/collections/${collection.slug}/${redirect}`);
      }

      initialRedirected.current = true;
    }
  }, [data?.collectionBySlug?._id]);

  const [toggleBookmark] = useMutation<IToggleBookmarkMutation, IToggleBookmarkMutationVariables>(
    ToggleBookmarkMutation,
    {
      variables: {
        targetId: data?.collectionBySlug?._id,
        targetType: IBookmarkType.Collection,
      },
      refetchQueries: [
        {
          query: SelfieCollectionsQuery,
        },
      ],
    },
  );
  const { countryOptions } = useCountryStateFilters({ country: stringOrFirst(router.query.country), state: "" });

  const tabsRef = createRef<HTMLInputElement>();

  useEffect(() => {
    if (!tabsRef.current) {
      return;
    }

    if (tab === "discussions") {
      tabsRef.current.scrollLeft = 200;
    }

    if (tab === "products") {
      tabsRef.current.scrollLeft = 999;
    }
  }, []);

  if (networkStatus === NetworkStatus.loading) {
    return (
      <Box display="flex" justifyContent="center" p={6}>
        <LoadingSpinner />
      </Box>
    );
  }
  if (!data || !data.collectionBySlug) {
    return (
      <Box>
        <PageGridContainer>
          <Box mt={8} textAlign="center">
            <LoadingSpinner />
          </Box>
        </PageGridContainer>
      </Box>
    );
  }

  const { collectionBySlug: collection } = data;

  const products = filterInUnionByTypenames(collection.content.items.map(mapFirstTag(collection.tags)), ["Product"]);
  const stories = filterInUnionByTypenames(collection.content.items.map(mapFirstTag(collection.tags)), ["Story"]);
  const groups = filterInUnionByTypenames(collection.content.items, ["Group"]);
  const projects = filterInUnionByTypenames(collection.content.items.map(mapFirstTag(collection.tags)), [
    "GovernmentProject",
    "GroupProject",
    "VendorProject",
  ]);
  const governments = filterInUnionByTypenames(collection.content.items.map(mapFirstTag(collection.tags)), [
    "Government",
  ]);

  return (
    <Box>
      <CollectionPageSeo collectionName={collection.name} tab={tab} />

      <Box bg="white" pt={8} borderBottom="1px" borderTop="1px" borderBottomColor="gray.100" borderTopColor="gray.100">
        <PageGridContainer>
          <Box display={["none", "none", "block"]}>
            <BackToCollectionsLink />
          </Box>

          <Box display={["block", "block", "none"]}>
            <MobileCollectionFilters router={router} countryOptions={countryOptions} collection={collection} />
          </Box>

          <Box>
            <Box
              display="flex"
              alignItems={["flex-start", "flex-start", "center"]}
              flexDirection={["column", "column", "row"]}
              justifyContent={[null, null, "space-between"]}
            >
              <IconButton
                display={["inline-flex", "inline-flex", "none"]}
                aria-label={collection.viewerDidBookmark ? "unbookmark collection" : "bookmark collection"}
                data-intercom-target="Bookmark collection"
                ml="auto"
                mb={2}
                variant="outline"
                variantColor="teal.800"
                size="sm"
                onClick={() => {
                  if (selfie) {
                    toggleBookmark();
                  } else {
                    signInModal.open();
                  }
                }}
                icon={(props) => (
                  <svg width="14" height="20" viewBox="0 0 14 20" fill="none" {...props}>
                    <path
                      d="M1 18.739L1.84784 17.9179L6.57356 13.3413L11.2992 17.9185L12.1471 18.7398V17.5594V2.62995C12.1471 1.74219 11.3984 1 10.4559 1H2.69118C1.74858 1 1 1.74152 1 2.62995V17.5587V18.739ZM6.92146 12.286L6.57361 11.9491L6.22574 12.286L2 16.3785V2.62995C2 2.31818 2.27617 2 2.69118 2H10.4559C10.8706 2 11.1471 2.31864 11.1471 2.62995V16.379L6.92146 12.286ZM6.92138 14.3743L6.57353 14.0373L6.22567 14.3742L1.58883 18.8648L1.58792 18.8657L1.58714 18.8664C1.58711 18.8665 1.58708 18.8665 1.58705 18.8665C1.40466 19.0439 1.13266 19.0953 0.897335 19.0004L0.896055 18.9999C0.659297 18.9051 0.499929 18.6784 0.5 18.4215V18.4214V2.62995C0.5 1.44861 1.48949 0.5 2.69118 0.5H10.4559C11.6577 0.5 12.6471 1.44942 12.6471 2.62995V18.4215C12.6471 18.7717 12.3568 19.0475 12.0089 19.0475H12.0085C11.8422 19.0472 11.6812 18.9836 11.5609 18.8681L11.5601 18.8673L11.5592 18.8665L6.92138 14.3743Z"
                      fill={collection.viewerDidBookmark ? "#28D094" : "white"}
                      stroke="#28D094"
                    />
                  </svg>
                )}
              >
                <FormattedMessage defaultMessage="Save" id="jvo0vs" />
              </IconButton>

              <Box display="flex" alignItems="center" width={["100%", "100%", "auto"]}>
                <Heading fontSize="3xl" maxWidth="400px" as="h1">
                  {collection.name}
                </Heading>

                {collection.packaging === ICollectionPackaging.Bundle && (
                  <Box ml={4} display={["none", "none", "inline-flex"]} pr={8} mt={1} mb="auto">
                    <MultipleFilterSelect
                      buttonText={intl.formatMessage({ defaultMessage: "Collections", id: "ulh3kf" })}
                      options={collection.tags.map((tag) => ({ value: tag.slug, label: tag.name }))}
                      selectedOptions={tags}
                      onChange={(tags) => {
                        const args = getRouterPushArgsFromSelectedValues({ tags }, router);

                        router.push(args.url, args.as);
                      }}
                    />
                  </Box>
                )}
              </Box>

              {collection.sponsoring !== ICollectionSponsoring.None &&
                collection.sponsoringVendors.length > 0 &&
                collection.sponsoringProducts.length === 0 && (
                  <Box mx={[null, null, "auto"]} maxWidth="400px" isTruncated={true} mb={[4, 4, "auto"]} mt={[4, 4, 1]}>
                    <Link
                      href={`/vendor?vendorSlug=${collection.sponsoringVendors[0].slug}`}
                      as={`/vendors/${collection.sponsoringVendors[0].slug}`}
                      passHref={true}
                      legacyBehavior={true}
                    >
                      <Anchor color="black" display="flex" alignItems="center">
                        <Text>
                          <FormattedMessage defaultMessage="Curated by" id="o4hmel" />
                        </Text>
                        <Avatar
                          src={collection.sponsoringVendors[0].logo}
                          name={collection.sponsoringVendors[0].name}
                          size="sm"
                          mx={2}
                        />
                        <Text fontWeight="bold" fontSize="lg">
                          {collection.sponsoringVendors[0].name}
                        </Text>
                      </Anchor>
                    </Link>
                  </Box>
                )}

              {collection.sponsoring !== ICollectionSponsoring.None &&
                collection.sponsoringVendors.length > 0 &&
                collection.sponsoringProducts.length > 0 && (
                  <Box mx={[null, null, "auto"]} maxWidth="400px" mb={[4, 4, "auto"]} mt={[4, 4, 1]}>
                    <Link
                      href={`/products/${collection.sponsoringProducts[0].slug}`}
                      passHref={true}
                      legacyBehavior={true}
                    >
                      <Anchor color="black" display="flex" alignItems="center">
                        <Text whiteSpace="nowrap">
                          <FormattedMessage defaultMessage="Curated by" id="o4hmel" />
                        </Text>
                        <Avatar
                          src={collection.sponsoringProducts[0].logo}
                          name={collection.sponsoringProducts[0].name}
                          size="sm"
                          mx={2}
                        />
                        <Text fontWeight="bold" fontSize="lg" clamp={2}>
                          {collection.sponsoringProducts[0].name}
                        </Text>
                      </Anchor>
                    </Link>
                  </Box>
                )}

              <Box display={["none", "none", "flex"]} mb="auto" mt={1}>
                <DesktopCollectionFilters router={router} countryOptions={countryOptions} />
              </Box>
            </Box>

            {collection.packaging === ICollectionPackaging.Bundle && (
              <Text color="black" fontSize="md" fontWeight="semibold">
                {collection.tags.map((tag) => tag.name).join(", ")}
              </Text>
            )}
          </Box>

          <Box
            display="flex"
            overflowX={["scroll", "scroll", "auto"]}
            ref={tabsRef}
            alignItems="center"
            bg="white"
            mt={4}
          >
            <CollectionTabLink
              icon={<ProjectsIcon />}
              label={intl.formatMessage({ defaultMessage: "Projects", id: "UxTJRa" })}
              count={collection.contentCounters.projectsCount}
              selected={!tab || tab === "projects"}
              tab="projects"
              slug={collection.slug}
            />

            <CollectionTabLink
              icon={<ProductsIcon />}
              label={intl.formatMessage({ id: "7NFfmz", defaultMessage: "Products" })}
              count={collection.contentCounters.productsCount}
              selected={tab === "products"}
              tab="products"
              slug={collection.slug}
            />

            <CollectionTabLink
              icon={<StoriesIcon />}
              label={intl.formatMessage({ defaultMessage: "Stories", id: "Ldxym4" })}
              count={collection.contentCounters.storiesCount}
              selected={tab === "stories"}
              tab="stories"
              slug={collection.slug}
            />

            <CollectionTabLink
              icon={<DiscussionsIcon />}
              label={intl.formatMessage({ defaultMessage: "Discussions", id: "S4r/19" })}
              count={collection.contentCounters.groupsCount}
              selected={tab === "discussions"}
              tab="discussions"
              slug={collection.slug}
            />

            {collection.tags.some((tag) => tag.types.includes(ITagType.Award)) && (
              <CollectionTabLink
                icon={<GovernmentsIcon />}
                label={intl.formatMessage({ defaultMessage: "Governments", id: "qnQX9U" })}
                count={collection.contentCounters.governmentsCount}
                selected={tab === "governments"}
                tab="governments"
                slug={collection.slug}
              />
            )}

            <Button
              display={["none", "none", "inline-flex"]}
              ml="auto"
              mr={3}
              variant="outline"
              variantColor="teal"
              size="sm"
              data-intercom-target="Bookmark collection"
              onClick={() => {
                if (selfie) {
                  toggleBookmark();
                } else {
                  signInModal.open();
                }
              }}
              rightIcon={(props) => (
                <svg width="14" height="20" viewBox="0 0 14 20" fill="none" {...props}>
                  <path
                    d="M1 18.739L1.84784 17.9179L6.57356 13.3413L11.2992 17.9185L12.1471 18.7398V17.5594V2.62995C12.1471 1.74219 11.3984 1 10.4559 1H2.69118C1.74858 1 1 1.74152 1 2.62995V17.5587V18.739ZM6.92146 12.286L6.57361 11.9491L6.22574 12.286L2 16.3785V2.62995C2 2.31818 2.27617 2 2.69118 2H10.4559C10.8706 2 11.1471 2.31864 11.1471 2.62995V16.379L6.92146 12.286ZM6.92138 14.3743L6.57353 14.0373L6.22567 14.3742L1.58883 18.8648L1.58792 18.8657L1.58714 18.8664C1.58711 18.8665 1.58708 18.8665 1.58705 18.8665C1.40466 19.0439 1.13266 19.0953 0.897335 19.0004L0.896055 18.9999C0.659297 18.9051 0.499929 18.6784 0.5 18.4215V18.4214V2.62995C0.5 1.44861 1.48949 0.5 2.69118 0.5H10.4559C11.6577 0.5 12.6471 1.44942 12.6471 2.62995V18.4215C12.6471 18.7717 12.3568 19.0475 12.0089 19.0475H12.0085C11.8422 19.0472 11.6812 18.9836 11.5609 18.8681L11.5601 18.8673L11.5592 18.8665L6.92138 14.3743Z"
                    fill={collection.viewerDidBookmark ? "#28D094" : "white"}
                    stroke="#28D094"
                  />
                </svg>
              )}
            >
              <FormattedMessage defaultMessage="Save" id="jvo0vs" />
            </Button>
          </Box>
        </PageGridContainer>
      </Box>

      <PageGridContainer>
        <Box mt={8}>
          <Box px={2} pb="32px" position="relative">
            {loading && networkStatus !== NetworkStatus.fetchMore && (
              <Box display="flex" justifyContent="center" py={6}>
                <LoadingSpinner />
              </Box>
            )}

            {(networkStatus === NetworkStatus.ready || collection.content.items.length > 0) && (
              <>
                {(!tab || tab === "projects") && (
                  <>
                    {projects.length === 0 && !loading && <NoProjects />}
                    <ProjectCards
                      routeParams={{
                        tab: "projects",
                      }}
                      onModalClose={() => {
                        const filteredCountries = queryString.stringify(
                          { ...omit(["collectionSlug", "projectSlug", "tab"], router.query) },
                          "&",
                        );
                        const queryStringParameters = filteredCountries?.length > 0 ? `?${filteredCountries}` : "";
                        router.push(
                          `/collections/[collectionSlug]/[[...tab]]`,
                          `/collections/${collection.slug}${queryStringParameters}`,
                        );
                      }}
                      projects={projects}
                      gridProps={{
                        columns: [1, 2, 3],
                        spacingX: 4,
                        spacingY: 6,
                      }}
                    />
                  </>
                )}
                {tab === "stories" && (
                  <>
                    {stories.length === 0 && !loading && <NoStories />}
                    <SimpleGrid columns={[1, 2, 3]} spacingX={4} spacingY={6}>
                      {stories.map((story) => {
                        const translationLinkWrap = (slug: string) => {
                          return (
                            <TranslationButton
                              paddingY="4px"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                router.push(`/stories/${slug}?lang=${locale}`);
                              }}
                            />
                          );
                        };

                        return (
                          <Link
                            href="/stories/[storySlug]"
                            as={`/stories/${story.slug}`}
                            passHref={true}
                            legacyBehavior={true}
                            key={story._id}
                          >
                            <StoryCardWithPlaceholder
                              story={{
                                ...hidePodcastTag(story),
                                government: story.storyGovernment,
                              }}
                              translationLinkWrap={locale !== "en-US" ? translationLinkWrap : undefined}
                            />
                          </Link>
                        );
                      })}
                    </SimpleGrid>
                  </>
                )}

                {tab === "products" && (
                  <>
                    {products.length === 0 && !loading && <NoProducts />}
                    <SimpleGrid columns={[1, 2, 3, 4]} spacingX={4} spacingY={6}>
                      {products.map((product) => {
                        const translationLinkHref = `/products/${product.slug}/${locale}`;
                        const translationLinkWrap = () => {
                          return (
                            <TranslationButton
                              paddingY="4px"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                router.push(translationLinkHref);
                              }}
                            />
                          );
                        };

                        return (
                          <Link
                            key={product._id}
                            href={`/products/${product.slug}`}
                            passHref={true}
                            legacyBehavior={true}
                          >
                            <ProductCard
                              product={product}
                              translationLinkWrap={
                                locale !== "en-US" && product.allowRequestTranslation ? translationLinkWrap : undefined
                              }
                            />
                          </Link>
                        );
                      })}
                    </SimpleGrid>
                  </>
                )}

                {tab === "discussions" && (
                  <>
                    {groups.length === 0 && !loading && <NoDiscussions />}
                    <SimpleGrid columns={[1, 2, 3, 4]} spacingX={4} spacingY={6}>
                      {groups.map((group) => {
                        if (group.viewerIsMember) {
                          return (
                            <Link
                              href={`/group?groupSlug=${group.slug}`}
                              as={`/groups/${group.slug}`}
                              passHref={true}
                              legacyBehavior={true}
                            >
                              <CollectionGroupCard group={group} key={group._id} />
                            </Link>
                          );
                        }

                        return <CollectionGroupCard group={group} key={group._id} />;
                      })}
                    </SimpleGrid>
                  </>
                )}

                {tab === "governments" && (
                  <>
                    {governments.length === 0 && !loading && <NoGovernments />}
                    <SimpleGrid columns={[1, 2, 3, 4]} spacingX={4} spacingY={6}>
                      {governments.map((government) => {
                        return (
                          <Link
                            key={government._id}
                            href="/governments/[governmentSlug]/[[...content]]"
                            as={`/governments/${government.slug}`}
                            passHref={true}
                            legacyBehavior={true}
                          >
                            <GovernmentWithTypeAndFlag government={government} />
                          </Link>
                        );
                      })}
                    </SimpleGrid>
                  </>
                )}
              </>
            )}

            {collection.content.pageInfo.hasNextPage &&
              [NetworkStatus.ready, NetworkStatus.fetchMore].includes(networkStatus) && (
                <Box display="flex" justifyContent="center" mt={4}>
                  <Button
                    variant="secondary"
                    loading={loading}
                    size="sm"
                    rightIcon={(props) => (
                      <svg width="24" height="12" viewBox="0 0 24 12" fill="none" {...props}>
                        <path
                          d="M2 2.5L11.7436 9.55882L22 2.5"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    )}
                    onClick={() => {
                      fetchMore({
                        variables: {
                          cursor: collection.content.pageInfo.cursor,
                        },
                        updateQuery: (current, { fetchMoreResult }) => {
                          if (!fetchMoreResult || !fetchMoreResult.collectionBySlug || !current.collectionBySlug) {
                            return {
                              __typename: "Query",
                              collectionBySlug: current.collectionBySlug,
                            };
                          }

                          return {
                            __typename: "Query",
                            collectionBySlug: {
                              ...current.collectionBySlug,
                              content: {
                                ...fetchMoreResult.collectionBySlug.content,
                                items: current.collectionBySlug.content.items.concat(
                                  fetchMoreResult.collectionBySlug.content.items,
                                ),
                              },
                            },
                          };
                        },
                      });
                    }}
                  >
                    <FormattedMessage defaultMessage="Show more" id="aWpBzj" />
                  </Button>
                </Box>
              )}
          </Box>
        </Box>
      </PageGridContainer>
    </Box>
  );
}

interface ICollectionFiltersProps {
  countryOptions: {
    label: string;
    value: string;
  }[];
  router: NextRouter;
}

function getRouterPushArgsFromSelectedValues(values: Record<string, string[]>, router: NextRouter) {
  const searchParams = queryString.stringify({ ...omit(["collectionSlug", "tab"], router.query), ...values }, "&");

  return {
    url: appendNextLinkHrefParams(router, {
      ...values,
    }),
    as: `${router.basePath}?${searchParams}`,
  };
}

function MobileCollectionFilters({
  countryOptions,
  router,
  collection,
}: ICollectionFiltersProps & { collection: NonNullable<ICollectionBySlugQuery["collectionBySlug"]> }) {
  const [isOpen, toggle] = useToggle(false);
  const [isLocationSelectOpen, toggleLocationSelectOpen] = useToggle(false);
  const [isTagsSelectOpen, toggleTagsSelectOpen] = useToggle(false);
  const intl = useIntl();

  function toggleLocationSelect() {
    toggleLocationSelectOpen();
    toggleTagsSelectOpen(false);
  }

  function toggleTagsSelect() {
    toggleTagsSelectOpen();
    toggleLocationSelectOpen(false);
  }

  return (
    <Box mb={3} data-intercom-target="Collection filters">
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Button variant="solid" bg="blue.200" size="sm" color="gray.800" leftIcon={FilterIcon} onClick={toggle}>
          <FormattedMessage defaultMessage="Filters" id="zSOvI0" />
        </Button>

        <BackToCollectionsLink />
      </Box>

      {isOpen && (
        <Box bg="blue.100" py={3} mx={-4} px={4} mt={3}>
          <MultipleFilterSelect
            buttonText={intl.formatMessage({ defaultMessage: "Location", id: "rvirM2" })}
            width="100%"
            isOpen={isLocationSelectOpen}
            onToggleOpen={toggleLocationSelect}
            options={countryOptions}
            selectedOptions={getCountriesFromRouter(router)}
            onChange={(countries) => {
              const args = getRouterPushArgsFromSelectedValues({ countries }, router);

              router.push(args.url, args.as);
            }}
          />

          {collection.packaging === ICollectionPackaging.Bundle && (
            <MultipleFilterSelect
              isOpen={isTagsSelectOpen}
              onToggleOpen={toggleTagsSelect}
              buttonText={intl.formatMessage({ defaultMessage: "Collections", id: "ulh3kf" })}
              mt={2}
              width="100%"
              options={collection.tags.map((tag) => ({ value: tag.slug, label: tag.name }))}
              selectedOptions={getTagsFromRouter(router)}
              onChange={(tags) => {
                const args = getRouterPushArgsFromSelectedValues({ tags }, router);

                router.push(args.url, args.as);
              }}
            />
          )}
        </Box>
      )}
    </Box>
  );
}

function DesktopCollectionFilters({ countryOptions, router }: ICollectionFiltersProps) {
  const intl = useIntl();

  return (
    <Box display="flex" alignItems="center" data-intercom-target="Collection filters">
      <Box display="flex" alignItems="center" mr={4} color="blue.600">
        <FilterIcon />

        <Text color="blue.600" fontSize="md" ml={2} fontWeight="semibold">
          <FormattedMessage defaultMessage="Filters" id="zSOvI0" />
        </Text>
      </Box>

      <MultipleFilterSelect
        buttonText={intl.formatMessage({ defaultMessage: "Location", id: "rvirM2" })}
        options={countryOptions}
        selectedOptions={getCountriesFromRouter(router)}
        onChange={(countries) => {
          const args = getRouterPushArgsFromSelectedValues({ countries }, router);

          router.push(args.url, args.as);
        }}
      />
    </Box>
  );
}

function FilterIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" {...props}>
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16 5H0V3H16V5ZM13.5999 9H2.3999V7H13.5999V9ZM4.7998 13H11.1998V11H4.7998V13Z"
        fill="currentColor"
      />
    </svg>
  );
}

function BackToCollectionsLink() {
  return (
    <Link href="/collections" passHref={true} legacyBehavior={true}>
      <Anchor display="flex" alignItems="center">
        <Box as="span" mr={2}>
          <svg width="9" height="8" viewBox="0 0 9 8" fill="none">
            <path
              d="M0.646447 3.64645C0.451185 3.84171 0.451184 4.15829 0.646446 4.35355L3.82843 7.53553C4.02369 7.7308 4.34027 7.7308 4.53553 7.53553C4.7308 7.34027 4.7308 7.02369 4.53553 6.82843L1.70711 4L4.53553 1.17157C4.7308 0.976312 4.7308 0.659729 4.53553 0.464467C4.34027 0.269205 4.02369 0.269204 3.82843 0.464467L0.646447 3.64645ZM2 3.5L1 3.5L1 4.5L2 4.5L2 3.5Z"
              fill="#086BE5"
            />
            <path
              d="M4.64645 3.64645C4.45118 3.84171 4.45118 4.15829 4.64645 4.35355L7.82843 7.53553C8.02369 7.7308 8.34027 7.7308 8.53553 7.53553C8.7308 7.34027 8.7308 7.02369 8.53553 6.82843L5.70711 4L8.53553 1.17157C8.7308 0.976312 8.7308 0.659729 8.53553 0.464467C8.34027 0.269205 8.02369 0.269204 7.82843 0.464467L4.64645 3.64645ZM6 3.5L5 3.5L5 4.5L6 4.5L6 3.5Z"
              fill="#086BE5"
            />
          </svg>
        </Box>
        <span>
          <FormattedMessage defaultMessage="Back to collections" id="vMEhBH" />
        </span>
      </Anchor>
    </Link>
  );
}

function getFilterFromTab(tab?: string | null) {
  if (tab === "projects") {
    return ICollectionItemType.Project;
  }

  if (tab === "products") {
    return ICollectionItemType.Product;
  }

  if (tab === "stories") {
    return ICollectionItemType.Story;
  }

  if (tab === "discussions") {
    return ICollectionItemType.Group;
  }
  if (tab === "governments") {
    return ICollectionItemType.Government;
  }

  return ICollectionItemType.Project;
}

function getTagsFromRouter(router: NextRouter): string[] {
  const query = router.query;

  if (!query.tags) {
    return [];
  }

  return Array.isArray(query.tags) ? query.tags : [query.tags];
}

function getCountriesFromRouter(router: NextRouter): string[] {
  const query = router.query;

  if (!query.countries) {
    return [];
  }

  return Array.isArray(query.countries) ? query.countries : [query.countries];
}

function mapFirstTag(tags: NonNullable<ICollectionBySlugQuery["collectionBySlug"]>["tags"]) {
  const collectionTagIds: string[] = tags.map((tag) => tag._id);

  return (item: NonNullable<ICollectionBySlugQuery["collectionBySlug"]>["content"]["items"][0]) => {
    if (item.__typename === "Group" || item.__typename === "Government") {
      return item;
    }

    const itemTagIds: string[] = (item.tags as ITag[]).map((tag) => tag._id);

    const intersect = intersectionWith(isEqual, itemTagIds, collectionTagIds);

    const foundTag = item.tags.find((tag) => tag._id === intersect[0]);
    const firstTag = foundTag ? [foundTag] : [];

    return {
      ...item,
      tags: uniqBy("_id", firstTag.concat(item.tags)),
    };
  };
}

const GovernmentWithTypeAndFlag = forwardRef<
  any,
  {
    government: Extract<
      NonNullable<ICollectionBySlugQuery["collectionBySlug"]>["content"]["items"][0],
      { __typename: "Government" }
    >;
  }
>(({ government, ...rest }, ref) => {
  return (
    <Flex align="center" overflow="hidden" as="a" ref={ref} {...rest}>
      <Avatar size="sm" name={government.name} src={government.logo} />
      <Box isTruncated={true}>
        <Box ml={3} isTruncated={true}>
          <Text fontSize="md" color="gray.600" isTruncated={true}>
            {government.name}
          </Text>
        </Box>
        <Box display="inline-flex" alignItems="center" ml={3} flex={1}>
          <CountryFlag width={24} height={14} countryCode={government.city.country as TCountryCode} />
          <Text fontSize="sm" color="blue.600" ml={2} mb={0}>
            {getCityType(government.city.governmentType)} - {government.city.countryName}
          </Text>
        </Box>
      </Box>
    </Flex>
  );
});

GovernmentWithTypeAndFlag.displayName = "GovernmentWithTypeAndFlag";
